import React, { useState } from 'react';
import { usePricingConfig } from '../../hooks/usePricingConfig';

export default function PricingCalculatorModal({ isOpen, onClose, t, language, onOpenDemo }) {
  const { config: pricingCfg } = usePricingConfig();
  const [students, setStudents] = useState('');

  if (!isOpen) return null;

  const CURR = pricingCfg.currency_symbol || '₹';
  const tiers = pricingCfg.pricing_tiers || [];
  const salesPhone = pricingCfg.sales_contact_phone || '9826618603';
  const count = parseInt(students, 10) || 0;
  const tier = count > 0 ? tiers.find((x) => count >= x.min && (x.max == null || count <= x.max)) : null;
  const isCustom = !!tier?.custom;
  const oneTime = tier && !isCustom ? tier.one_time : 0;
  const maint = tier && !isCustom ? tier.annual_maintenance : 0;
  const total = oneTime + maint;
  const fmt = (n) => `${CURR}${Number(n).toLocaleString('en-IN')}`;

  const salesUrl = `https://wa.me/91${salesPhone}?text=${encodeURIComponent(language === 'hi' ? `नमस्ते! हमारे स्कूल में ${count || '5000+'} छात्र हैं। कृपया Shiksha Mitra custom pricing बताएँ।` : `Hi! Our school has ${count || '5000+'} students. Please share Shiksha Mitra custom pricing.`)}`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto transform transition-all" onClick={(e) => e.stopPropagation()} data-testid="pricing-calculator-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 rounded-t-3xl">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                <span>🧮</span> {language === 'hi' ? 'Pricing Calculator' : 'Pricing Calculator'}
              </h3>
              <p className="text-orange-100 text-sm mt-1">
                {language === 'hi' ? '100% FREE Platform • One-time + सालाना maintenance' : '100% FREE Platform • One-time + yearly maintenance'}
              </p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition text-3xl font-light" data-testid="pricing-calc-close">&times;</button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Student Count Input */}
          <div className="flex flex-col items-center mb-6">
            <p className="text-gray-600 mb-4 text-center">
              {language === 'hi' ? 'आपके स्कूल में कुल कितने users (छात्र+शिक्षक+staff)?' : 'How many total users (students+teachers+staff)?'}
            </p>
            <input
              type="number"
              min="0"
              value={students}
              onChange={(e) => setStudents(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder={language === 'hi' ? 'जैसे: 250' : 'e.g. 250'}
              className="w-40 text-center text-4xl font-bold text-gray-800 border-b-2 border-orange-400 focus:border-orange-500 outline-none bg-transparent"
              data-testid="pricing-calc-students"
            />
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {[200, 500, 1000, 2000, 4000].map((c) => (
                <button
                  key={c}
                  onClick={() => setStudents(String(c))}
                  className={`px-4 py-2 rounded-full font-medium transition ${count === c ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-orange-100'}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Result */}
          {isCustom ? (
            <div className="bg-slate-900 text-white rounded-2xl p-5 mb-6 text-center" data-testid="pricing-calc-custom">
              <p className="font-semibold text-amber-300 mb-1">{language === 'hi' ? '🏛️ बड़े स्कूल (5000+ छात्र)' : '🏛️ Large school (5000+ students)'}</p>
              <p className="text-sm text-gray-300 mb-3">{language === 'hi' ? 'आपके स्कूल के लिए custom pricing है।' : 'Custom pricing for your school.'}</p>
              <a href={salesUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 rounded-lg font-bold text-white transition" data-testid="pricing-calc-contact-sales">
                <i className="fa-brands fa-whatsapp"></i> {language === 'hi' ? 'Sales से संपर्क करें' : 'Contact Sales'}
              </a>
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-orange-200 bg-orange-50 p-5 mb-6" data-testid="pricing-calc-result">
              {tier && (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">{language === 'hi' ? 'आपका plan' : 'Your plan'}</span>
                  <span className="text-sm font-bold text-gray-800" data-testid="pricing-calc-tier">{language === 'hi' ? tier.label_hi : tier.label_en}</span>
                </div>
              )}
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600">{language === 'hi' ? 'One-time (license + setup)' : 'One-time (license + setup)'}</span>
                <span className="text-sm font-semibold text-gray-800">{count > 0 ? fmt(oneTime) : '—'}</span>
              </div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-gray-600">{language === 'hi' ? 'सालाना maintenance' : 'Annual maintenance'}</span>
                <span className="text-sm font-semibold text-gray-800">{count > 0 ? `${fmt(maint)}${language === 'hi' ? '/साल' : '/yr'}` : '—'}</span>
              </div>
              <div className="border-t border-orange-200 pt-3 flex justify-between items-center">
                <span className="font-bold text-gray-800">{language === 'hi' ? 'अभी देय (पहला भुगतान)' : 'Payable now (first payment)'}</span>
                <span className="text-3xl font-black text-green-600" data-testid="pricing-calc-total">{count > 0 ? fmt(total) : `${CURR}0`}</span>
              </div>
            </div>
          )}

          {/* Setup Includes */}
          {t?.pricing?.setupIncludes && (
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <p className="text-sm font-semibold text-green-700 mb-2 text-center">
                {language === 'hi' ? '✅ Setup में शामिल:' : '✅ Setup Includes:'}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {t.pricing.setupIncludes.map((item, idx) => (
                  <span key={idx} className="bg-white px-3 py-1.5 rounded-full text-sm text-gray-600 border border-green-200">✓ {item}</span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-6 text-center">
            <button
              onClick={() => { onClose(); onOpenDemo(); }}
              className="px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full font-semibold hover:from-orange-600 hover:to-amber-600 transition shadow-lg shadow-orange-500/30"
            >
              {language === 'hi' ? '📞 Free Demo Book करें' : '📞 Book Free Demo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
